import type { ReactNode } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { MESSAGE_MAX_LENGTH } from "../lib/seasonForm"
import { PhoneMessagePreview, WebsiteMessagePreview } from "./MessagePreview"
import { FieldIssues, issuesFor, type SeasonFieldsProps } from "./SeasonFields"

/** The three closed messages: what the public is told while booking is closed. */
export function ClosedMessagesCard({ form, issues, onChange }: SeasonFieldsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Closed messages</CardTitle>
        <CardDescription>
          What the public is told whenever booking is closed — before the booking window, after it, or after "Close
          booking now". Staff booking is never blocked.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <MessageField
          id="season-web-message"
          label="Website message"
          hint="Shown on the public booking page."
          value={form.closedWebMessage}
          issues={issuesFor(issues, "closedWebMessage")}
          onChange={(closedWebMessage) => onChange({ closedWebMessage })}
          preview={<WebsiteMessagePreview message={form.closedWebMessage} />}
        />

        <MessageField
          id="season-phone-english"
          label="Phone message (English)"
          hint={'Read aloud to callers by text-to-speech. Spell web addresses out as they should be spoken, e.g. "P S E dot com" rather than "pse.com".'}
          value={form.closedPhoneMessageEnglish}
          issues={issuesFor(issues, "closedPhoneMessageEnglish")}
          onChange={(closedPhoneMessageEnglish) => onChange({ closedPhoneMessageEnglish })}
          preview={<PhoneMessagePreview message={form.closedPhoneMessageEnglish} />}
        />

        <MessageField
          id="season-phone-spanish"
          label="Phone message (Spanish)"
          optional
          hint={'Read aloud to callers who choose Spanish. Leave it blank and they hear the English message. Spell web addresses out, e.g. "P S E punto com".'}
          value={form.closedPhoneMessageSpanish}
          issues={issuesFor(issues, "closedPhoneMessageSpanish")}
          onChange={(closedPhoneMessageSpanish) => onChange({ closedPhoneMessageSpanish })}
          preview={
            <PhoneMessagePreview message={form.closedPhoneMessageSpanish} fallback={form.closedPhoneMessageEnglish} />
          }
        />
      </CardContent>
    </Card>
  )
}

interface MessageFieldProps {
  id: string
  label: string
  hint: string
  optional?: boolean
  value: string
  issues: string[]
  onChange: (value: string) => void
  preview: ReactNode
}

function MessageField({ id, label, hint, optional, value, issues, onChange, preview }: MessageFieldProps) {
  const length = value.trim().length

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {label}
          {optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
        </Label>
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
        <Textarea
          id={id}
          value={value}
          rows={5}
          aria-invalid={issues.length > 0}
          aria-describedby={`${id}-hint ${id}-issues`}
          onChange={(e) => onChange(e.target.value)}
        />
        <div className="flex items-start justify-between gap-2">
          <FieldIssues id={`${id}-issues`} issues={issues} />
          <span
            className={cn("ml-auto text-xs tabular-nums", length > MESSAGE_MAX_LENGTH ? "text-destructive" : "text-muted-foreground")}
          >
            {length} / {MESSAGE_MAX_LENGTH}
          </span>
        </div>
      </div>
      {preview}
    </div>
  )
}
